import { z } from "zod";

import type { ExtendedWorkspace, MeetingModelJson, MeetingTranscriptModelJson } from "@performyard/service-contracts";

import type { Goal } from "../../../api/services/searchServiceResultTypes/goal";

/**
 * Available actions for goals in meetings
 */
export type MeetingGoalAction = "addToAgenda" | "edit" | "update" | "addMessage" | "complete" | "archive" | "reopen";

/**
 * Status types for checklist items (agenda/action items)
 */
export type ItemStatus = "complete" | "incomplete" | "deleted";

/**
 * Type of checklist item
 */
export type ItemType = "agenda" | "action";

/**
 * Notes type selection for dropdowns
 */
export type NoteType = "my-shared" | "my-private" | "other-shared";

/**
 * Context for assigning action items
 */
export type UserAssignmentContext = {
	actionItemId: string;
	meetingId: string;
	currentAssigneeId?: string;
	dueDate?: Date | null;
	name?: string;
} | null;

/**
 * Props for the MeetingContext provider
 */
export type MeetingContextType = {
	meeting: MeetingModelJson;
	userId: string;
	setEditingMeeting: (meeting: MeetingModelJson) => void;
	setUserAssignment: (context: NonNullable<UserAssignmentContext>) => void;
	participantId?: string;
	participantName?: string;
	workspace?: ExtendedWorkspace;
	isInteractionDisabled?: boolean;
	isAdminView?: boolean;
	isAdmin?: boolean;
	isManagerDirectReportMeeting?: boolean;
};

/**
 * Assignment click handler arguments
 */
export type AssignmentClickArgs = {
	itemId: string;
	meetingId: string;
	currentAssigneeId?: string;
	dueDate?: Date | null;
	name?: string;
};

/**
 * Core data for a checklist item
 */
export type ChecklistItemData = {
	id: string;
	name: string;
	dueDate?: Date | null;
	status: ItemStatus;
	creatorId: string;
	assigneeId?: string;
	assigneeName?: string;
};

/**
 * Event handlers for checklist item interactions
 */
export type ChecklistItemHandlers = {
	onEdit: (id: string, name: string) => void;
	onNameChange: (value: string) => void;
	onStatusChange: (status: ItemStatus) => void;
	onSave: (save: boolean) => void;
	onAssignmentClick?: (args: AssignmentClickArgs) => void;
};

/**
 * Base props shared by all checklist items
 */
type ChecklistItemBaseProps = ChecklistItemData &
	ChecklistItemHandlers & {
		userId: string;
		isEditing: boolean;
		isNewItem?: boolean;
		meetingId?: string;
		itemType: ItemType;
		currentEditValue: string;
	};

/**
 * Props for checklist item component
 * Goal-related props are optional, but onGoalActionSelect should be provided when goalId is present
 */
export type ChecklistItemProps = ChecklistItemBaseProps & {
	goalId?: string;
	goal?: Goal;
	onGoalActionSelect?: (action: MeetingGoalAction, goal: Goal) => void;
};

/**
 * Schema for assignment form validation
 */
export const assignmentFormSchema = z.object({
	assignee: z
		.object({
			value: z.string(),
			label: z.string(),
		})
		.or(z.null()),
	dueDate: z
		.date()
		.refine((date) => date > new Date(), {
			message: "Due date must be in the future",
		})
		.or(z.null()),
});

export type AssignmentFormData = z.infer<typeof assignmentFormSchema>;
export type AssignmentValidationErrors = Partial<Record<keyof AssignmentFormData, { message: string }>>;

/**
 * Parsed notes data structure
 */
export type NotesData = {
	primaryPublicNotes: {
		_id: string;
		content: string;
	} | null;
	secondaryPublicNotes: {
		_id: string;
		content: string;
	} | null;
	userPrivateNotes: {
		_id: string;
		content: string;
	} | null;
	isPrimaryUserNotes: boolean;
	primaryNotesOwnerName: string;
	secondaryNotesOwnerName: string;
};

export type TranscriptData = MeetingTranscriptModelJson | null | undefined;
